import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { VelavuDevice } from "velavu-js-api";
import DynamicBattery from "../../../elements/dynamic-battery";
import StatusIndicator from "../../../elements/status-indicator";
import VelavuButton from "../../../elements/velavu-button";
import VelavuItemIcon from "../../../elements/velavu-item-icon";
import VelavuNotice from "../../../elements/velavu-notice/velavu-notice";
import VelavuSimpleTable, {
	CenteredTD,
	MultilineTD,
} from "../../../elements/velavu-simple-table";
import {
	mapConnectionStatus,
	mapDeviceModel,
} from "../../../helper/language-helper";
import { getDeviceStatus } from "../../../helper/status-helper";
import * as styles from "./modal-register-tag-details.module.scss";
import LocationDescription from "../../dashboard/dropdown/location-description";

interface ModalRegisterTagDetailsProps {
	tag: VelavuDevice;
	onClose?: VoidFunction;
	onRepeat?: VoidFunction;
}

function TagStatus(props: { tag: VelavuDevice }) {
	const { t } = useTranslation();
	const status = useMemo(() => {
		return getDeviceStatus(props.tag);
	}, [props.tag]);

	return (
		<>
			<StatusIndicator status={status} />
			<span style={{ marginLeft: 8 }}>
				{mapConnectionStatus(status, t)}
			</span>
		</>
	);
}

export default function ModalRegisterTagDetails(
	props: ModalRegisterTagDetailsProps,
) {
	const { t } = useTranslation();

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<VelavuItemIcon data={{ model: props.tag.model }} />
				<div className={styles.headerText}>
					<span className={styles.headerTitle}>{props.tag.id}</span>
					<span className={styles.headerSubtitle}>
						{mapDeviceModel(props.tag.model, t)}
					</span>
				</div>
			</div>

			<VelavuSimpleTable>
				<tbody>
					<tr>
						<td>{t("devices.register.tag_id")}</td>
						<td>{props.tag.id}</td>
					</tr>
					<tr>
						<td>{t("common.status")}</td>
						<CenteredTD>
							<TagStatus tag={props.tag} />
						</CenteredTD>
					</tr>
					{props.tag.state?.power && (
						<tr>
							<td>{t("devices.table_battery")}</td>
							<CenteredTD>
								<span style={{ marginRight: 4 }}>
									{props.tag.state?.power?.battery_level !==
									undefined
										? `${props.tag.state.power.battery_level}%`
										: t("common.na")}
								</span>
								<DynamicBattery
									resource={props.tag.state.power}
								/>
							</CenteredTD>
						</tr>
					)}
					{props.tag.state && (
						<tr>
							<td>{t("devices.register.firmware")}</td>
							<td>{props.tag.state.app_version}</td>
						</tr>
					)}
					<tr>
						<td>{t("devices.register.location")}</td>
						<MultilineTD>
							<LocationDescription
								device={props.tag}
								textClassName={null}
							/>
						</MultilineTD>
					</tr>
				</tbody>
			</VelavuSimpleTable>

			<div className={styles.spacer} />

			<VelavuNotice type="confirm">
				{t("devices.register.success")}
			</VelavuNotice>
			<div className={styles.buttonBar}>
				<VelavuButton
					label={t("devices.register.register_another")}
					onClick={props.onRepeat}
					variant="outlined"
					fullWidth
				/>
				<div className={styles.buttonBarSpacer} />
				<VelavuButton
					label={t("devices.register.done")}
					onClick={props.onClose}
					fullWidth
				/>
			</div>
		</div>
	);
}
